import { ChevronRight } from "lucide-react";
import React from 'react';
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, Chat, Logo } from "../configs";
import { API_ENDPOINTS, get } from "../configs/api";

export const LicensesPage = () => {
    const { search } = useLocation();
    const query = new URLSearchParams(search);

    const pan = query.get('tax_no');
    const firmName = query.get('state');
    const navigate = useNavigate();
    const [licenseData, setLicenseData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [apiResponse, setApiResponse] = useState(null);

    useEffect(() => {
        if (!pan || !firmName) {
            navigate('/unauthorized');
            return;
        }

        const fetchStatuses = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const response = await get(API_ENDPOINTS.license_status(firmName, pan));
                setApiResponse(response);

                const seedDoc = response.license_documents?.find(doc => doc.document_license_type.toUpperCase() === 'SEED');
                const pesticideDoc = response.license_documents?.find(doc => doc.document_license_type.toUpperCase() === 'PESTICIDE');

                const transformDocument = (doc) => {
                    if (!doc) return null;
                    return {
                        id: doc.id,
                        licenseNo: doc.license_number,
                        issueDate: doc.license_issue_date,
                        expiryDate: doc.license_expiry_date,
                        reapplicationDate: doc.license_reapplication_date || null,
                        licenseCopyUploaded: doc.document_urls || [],
                        reapplicationProofUploaded: doc.document_acknowledgement_urls || [],
                        rejectedReason: doc.rejected_reason,
                    };
                };

                const getStatus = (doc) => {
                    if (!doc?.approval_status) return null;
                    const status = doc.approval_status.toLowerCase();
                    switch (status) {
                        case 'approved':
                            return 'approved';
                        case 'rejected':
                        case 'expired':
                            return 'rejected';
                        case 'under_review':
                            return 'under_review';
                        default:
                            return null;
                    }
                };

                const pesticideStatus = getStatus(pesticideDoc);
                const seedStatus = getStatus(seedDoc);

                const data = {
                    pesticide: transformDocument(pesticideDoc),
                    seed: transformDocument(seedDoc),
                    pesticideStatus,
                    seedStatus,
                };

                if (data.pesticide) data.pesticide.status = pesticideStatus;
                if (data.seed) data.seed.status = seedStatus;

                setLicenseData(data);
            } catch (error) {
                console.error("Failed to fetch license statuses:", error);
                setError("Failed to load license data. Please try again later.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchStatuses();
    }, [pan, firmName, navigate]);


    const handleRowClick = (type, path) => {
        const status = type === 'seed' ? licenseData?.seedStatus : licenseData?.pesticideStatus;

        if (!status) {
            // Go to details page and prompt user to upload there
            navigate(`${path}/${pan}`, {
                state: {
                    firmName: decodeURIComponent(firmName),
                    apiResponse,
                    promptUpload: true,
                }
            });
        } else {
            const editData = type === 'seed' ? licenseData?.seed : licenseData?.pesticide;
            navigate(`${path}/${pan}`, {
                state: {
                    editData,
                    status,
                    firmName: decodeURIComponent(firmName),
                    apiResponse,
                }
            });
        }
    };

    const renderRow = (Icon, title, status, subtitle, path, type) => {
        const isWarning = !status;
        const needsUpload = !status;
        const currentdata = type === 'seed' ? licenseData?.seed : licenseData?.pesticide;
        return (
            <div
                className={`row license-row ${needsUpload ? 'license-row--upload' : ''}`}
                onClick={() => handleRowClick(type, path)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowClick(type, path); } }}
                aria-label={needsUpload ? `${title}: ${subtitle}. Tap to upload.` : `${title}: view or edit`}
            >
                <div className="row-left">
                    <div className="icon-box">
                        <img src={Icon} height={48} alt="" />
                    </div>
                    <div className="row-text">
                        <h4>{title}</h4>
                        {!status ? <p className={isWarning ? "row-subtitle row-subtitle--action" : ""}>{subtitle}</p> : <>
                            {/* <span className="validTill">Valid Till: {currentdata['expiryDate'] ? currentdata['expiryDate'] : 'No expiry'}</span> */}
                            {status === 'approved' && <span className="badge badge-active">Accepted</span>}
                            {status === 'rejected' && <span className="badge badge-rejected">Rejected</span>}
                            {status === 'under_review' && <span className="badge badge-in_progress">Under Review</span>}
                        </>}
                        {status === 'rejected' && <div className="rejected-reason">{currentdata['rejectedReason']}</div>}
                        
                    </div>
                   
                </div>
                <div className="row-right">
                     {needsUpload && <div className="upload-bt">Add Details <ChevronRight size={20} className="row-chevron" aria-hidden /></div>}
                </div>
            </div>
        );
    };

    const pesticideStatus = licenseData?.pesticideStatus;
    const seedStatus = licenseData?.seedStatus;


    let infoCardText = null;

    if (apiResponse && apiResponse.is_seed_business_distributor && !pesticideStatus && apiResponse.is_pesticide_business_distributor && !seedStatus) {
        infoCardText = "Add your pesticide & seed license. Billing will be paused if not submitted.";
    }
    else if (apiResponse && apiResponse.is_pesticide_business_distributor && !pesticideStatus) {
        infoCardText = "Add your pesticide license. Billing will be paused if not submitted.";
    } else if (apiResponse && apiResponse.is_seed_business_distributor && !seedStatus) {
        infoCardText = "Add your seed license. Billing will be paused if not submitted.";
    }

    return (
        <div className="container">
            {isLoading && (
                <div className="loader-overlay">
                    <div className="loader-spinner" />
                    <p className="loader-text">Loading, please wait...</p>
                </div>
            )}

            <div className="header">
                {/* <ChevronLeft size={24} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} /> */}
                <img src={Logo} height={40} alt="logo" />
                <h1>Licenses & Certificates</h1>
            </div>
            <span className="heading">Update your License Details</span>
            {!error && apiResponse && (
                <div className="info-bar">
                    <div className="header-cont">
                        <div className="info-row">
                        <span className="info-label">Firm Name</span>
                        <span className="info-value">{apiResponse.distributor_name}</span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">Bayer Customer ID's:</span>
                        <span className="info-value">{apiResponse.sap_ids.join(",")}</span>
                    </div>
                    
                        <div className="info-row">
                            <span className="info-label">PAN:</span>
                            <span className="info-value">{apiResponse.tax_number}</span>
                        </div>
                        <div className="info-row">
                            <span className="info-label">State:</span>
                            <span className="info-value">{apiResponse.state_name}</span>
                        </div>
                  
                    </div>

                </div>
            )}
            <div className="main-container">
                <div className="page-content licenses-page-content">
                    {error ? (
                        <div className="error-alert">{error}</div>
                    ) : (
                        <div className="main-content">
                         
                           
                            <div className="card">
                                <div className="card-label">Licenses</div>
                                {infoCardText && (
                                    <div className="infoCard">
                                        {infoCardText}
                                    </div>
                                )}
                                {apiResponse?.is_seed_business_distributor && renderRow(Card, "Seed License", licenseData?.seedStatus, "Add your seed license", "/details/seed", "seed")}
                                {apiResponse?.is_pesticide_business_distributor && renderRow(Chat, "Pesticide License", licenseData?.pesticideStatus, "Add your pesticide license", "/details/pesticide", "pesticide")}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};